import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { blogPosts } from '../data/blog';

// ✅ Direct imports from your bimages folder
import imgWebDev      from '../data/bimages/Codeviora-blog-1.webp';
import imgAppDev      from '../data/bimages/Codeviora-blog-2.webp';
import imgDesign      from '../data/bimages/Codeviora-blog-3.webp';
import imgSEO         from '../data/bimages/Codeviora-blog-4.webp';
import imgMarketing   from '../data/bimages/Codeviora-blog-5.webp';
import imgAI          from '../data/bimages/Codeviora-blog-6.webp';

const ALL_CATS = ['All', ...new Set(blogPosts.map(p => p.category))];
const PAGE_SIZE = 7;

const BLOG_IMAGES = {
  'Web Development'    : imgWebDev,
  'App Development'    : imgAppDev,
  'Web Design'         : imgDesign,
  'SEO'                : imgSEO,
  'Digital Marketing'  : imgMarketing,
  'Artificial Intelligence': imgAI,
};

const FALLBACK_IMAGE = imgWebDev;

// Generates a consistent pseudo view-count per post (based on slug)
function getViewCount(slug) {
  let hash = 0;
  for (let i = 0; i < slug.length; i++) {
    hash = (hash << 5) - hash + slug.charCodeAt(i);
    hash |= 0;
  }
  const views = 800 + (Math.abs(hash) % 4200); // range: 800 – 5000
  return views.toLocaleString();
}

function ViewsBadge({ slug, className = '' }) {
  return (
    <span className={`text-xs flex items-center gap-1 ${className}`}>
      <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
        <path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
        <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.3"/>
      </svg>
      {getViewCount(slug)} views
    </span>
  );
}

export default function Blog() {
  useScrollReveal();
  const [active, setActive] = useState('All');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  // Latest blog (highest id) pehle aayega
  const sorted   = [...blogPosts].sort((a, b) => b.id - a.id);
  const filtered  = active === 'All' ? sorted : sorted.filter(p => p.category === active);
  const [featured, ...rest] = filtered;
  const visibleRest = rest.slice(0, visibleCount - 1);
  const hasMore     = rest.length > visibleRest.length;

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
    const timer = setTimeout(() => {
      document.querySelectorAll('.reveal').forEach(el => el.classList.add('visible'));
      document.querySelectorAll('.img-reveal').forEach(el => el.classList.add('visible'));
    }, 100);
    return () => clearTimeout(timer);
  }, [active]);

  return (
    <>
      <SEO
        title="Blog — Web Development, SEO, Design & Marketing Insights"
        description="Practical insights on web development, SEO, app development, web design, and digital marketing from the CodeViora team."
        canonical="/blog"
      />

      {/* Hero */}
      <section className="bg-grid-black pt-16 pb-16 md:pt-20 md:pb-20 relative overflow-hidden">
        <div className="container-max relative">
          <div className="max-w-2xl animate-fade-up">
            <span className="section-label-inv">Blog</span>
            <h1 className="section-title-inv text-5xl md:text-6xl mt-2">INSIGHTS ON DIGITAL GROWTH.</h1>
            <p className="section-sub-inv">
              Practical articles on web development, SEO, design, and digital marketing — written by people who do this work every day.
            </p>
          </div>
        </div>
      </section>

      {/* Filter */}
      <div className="bg-pure-black border-b border-white/10 sticky top-0 z-40 border-white/10 rounded-b-[30px] md:rounded-b-[100px]">
        <div className="container-max">
          <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-none">
            {ALL_CATS.map(cat => (
              <button
                key={cat}
                onClick={() => setActive(cat)}
                className={`flex-shrink-0 px-4 py-2 rounded-lg text-xs font-medium transition-colors tracking-wide ${
                  active === cat
                    ? 'bg-white dark:bg-[#111] text-black dark:text-white'
                    : 'text-white/40 hover:text-white hover:bg-white/5 dark:bg-white/5'
                }`}
                aria-pressed={active === cat}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </div>

      <section className="bg-grid-white py-16 md:py-20">
        <div className="container-max">
          {filtered.length === 0 && (
            <p className="text-black/30 dark:text-white/30 text-center py-20">No posts in this category yet.</p>
          )}

          {/* Featured */}
          {featured && (
            <Link
              key={`featured-${active}`}
              to={`/blog/${featured.slug}`}
              className="card-white group flex flex-col md:flex-row overflow-hidden mb-10"
            >
              <div className="md:w-2/5 h-52 md:h-auto flex-shrink-0 overflow-hidden rounded-t-2xl md:rounded-l-2xl md:rounded-tr-none">
                <img
                  src={BLOG_IMAGES[featured.category] || FALLBACK_IMAGE}
                  alt={featured.title}
                  className="img-reveal w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              </div>
              <div className="p-8 flex flex-col justify-center flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-xs font-medium text-black dark:text-white bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10 px-2.5 py-1 rounded-full">
                    {featured.category}
                  </span>
                  <span className="text-xs text-black/30 dark:text-white/30">{featured.readTime}</span>
                  {/* <ViewsBadge slug={featured.slug} className="text-black/30 dark:text-white/30" /> */}
                </div>
                <h2
                  className="text-xl md:text-2xl text-black dark:text-white leading-snug mb-3 group-hover:text-[#ff6b2b] dark:group-hover:text-[#ff6b2b] transition-colors"
                  style={{ fontFamily: 'Anton,sans-serif', fontWeight: 500 }}
                >
                  {featured.title}
                </h2>
                <p className="text-sm text-black/50 dark:text-white/50 leading-relaxed mb-5">{featured.excerpt}</p>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-black flex items-center justify-center text-white text-xs font-black">CV</div>
                  <div>
                    <p className="text-xs font-medium text-black dark:text-white">{featured.author.name}</p>
                    <p className="text-xs text-black/30 dark:text-white/30">{featured.date}</p>
                  </div>
                </div>
              </div>
            </Link>
          )}

          {/* Grid */}
          {visibleRest.length > 0 && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {visibleRest.map(({ slug, title, excerpt, category, date, readTime, author }) => (
                <Link
                  key={`${active}-${slug}`}
                  to={`/blog/${slug}`}
                  className="card-white group overflow-hidden flex flex-col"
                >
                  <div className="h-44 overflow-hidden rounded-t-2xl">
                    <img
                      src={BLOG_IMAGES[category] || FALLBACK_IMAGE}
                      alt={title}
                      className="img-reveal w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xs font-medium text-black dark:text-white bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10 px-2.5 py-1 rounded-full">
                        {category}
                      </span>
                      <span className="text-xs text-black/25 dark:text-white/25">{readTime}</span>
                    </div>
                    <h2
                      className="font-medium text-black dark:text-white leading-snug mb-2 group-hover:text-[#ff6b2b] dark:group-hover:text-[#ff6b2b] transition-colors flex-1 line-clamp-2"
                      style={{ fontFamily: 'Anton,sans-serif', fontWeight: 500 }}
                    >
                      {title}
                    </h2>
                    <p className="text-sm text-black/50 dark:text-white/50 leading-relaxed mb-4 line-clamp-2">{excerpt}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-black flex items-center justify-center text-white text-xs font-black">CV</div>
                        <p className="text-xs font-medium text-black/60 dark:text-white/60">{author.name}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {/* <ViewsBadge slug={slug} className="text-black/25 dark:text-white/25" /> */}
                        <p className="text-xs text-black/25 dark:text-white/25">{date}</p>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {/* Load More */}
          {hasMore && (
            <div className="flex justify-center mt-12">
              <button
                onClick={() => setVisibleCount(c => c + PAGE_SIZE)}
                className="btn-outline text-sm px-8"
              >
                Load More Posts
              </button>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
